import type { Auth, User } from "firebase/auth";
import { signOut } from "./signOut";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as api from "./api";
import { AreaToggle, useAreaShapes } from "./AreaLayer";
import { dayTitle } from "./driverRoutes";
import { clockTime } from "./format";
import { go, useTitle } from "./hooks";
import { accountName, dotTitle, fillWords, floatAgrees, floatShare, floatWords, levelDot, sideWords, tankSide } from "./fill";
import { ClusterKey, FillBandKeys, LevelDot } from "./LevelDot";
import { type MapInfo, MapInfoBox } from "./MapInfoBox";
import { MapFullscreenButton } from "./MapFullscreen";
import { useMarkerCone } from "./ConeCard";
import { placeable, statusLabel } from "./mapView";
import { SkeletonBar, SkeletonFrame } from "./Skeleton";
import { type GoogleMap, loadMaps, type Maps, type Marker, type Polyline, watchMapErrors } from "./maps";
import {
  AGE_TEXT,
  allPickupsText,
  bars,
  changeText,
  dateText,
  dayNames,
  FAILURE_TEXT,
  type Failure,
  failure,
  galShort,
  galWhole,
  progressWidth,
  lookPath,
  lookText,
  reported,
  reportText,
  routeGalText,
  routeStatusText,
  routesPath,
  routeText,
  segments,
  shortName,
  since,
  todayPoints,
  STATE_TEXT,
  STATES,
  statusTitle,
  svgLength,
  weeksLabel,
  weekTop,
  wellText,
} from "./overview";
import { CHECK_PIN, usePinLayer } from "./PinLayer";
import { truckColour } from "./routePlan";
import { sortRows, type SortColumn } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { HelpText } from "./Hint";
import { RefreshFailed, refreshFailure } from "./RefreshFailed";
import { failedText, keptText } from "./store/accounts";
import { OVERVIEW } from "./store/pageViews";
import { useView, type ViewState } from "./store/useView";

type Load = api.Overview | Failure | undefined;
type MapState = "waiting" | "loading" | "ready" | "failed";

const CHART_W = 240;
const CHART_H = 44;
const BAR_W = 120;
const ALL_ACCOUNTS: Record<string, string> = {};
const PIN_Z = 100;
const FINISHED = new Set<api.OverviewState>(["done", "checked", "skipped"]);

function send(user: User, entry: api.OverviewLog) {
  console.info("overview:", JSON.stringify(entry));
  void user
    .getIdToken()
    .then((token) => api.overviewLog(token, entry))
    .catch(() => console.warn("overview: the load log did not reach the server"));
}

function navigate(path: string) {
  history.pushState(null, "", path);
  dispatchEvent(new PopStateEvent("popstate"));
}

function element(className: string, text?: string, colour?: string): HTMLElement {
  const found = document.createElement("div");
  found.className = className;
  if (text) found.textContent = text;
  if (colour) found.style.setProperty("--truck", colour);
  return found;
}

/** The account pins of the Accounts map, with the stops by state, the last report of each truck and the full or alarm tanks on top. */
function OverviewMap({ user, found, base, timeZone, onState }: { user: User; found: api.Overview; base: string; timeZone?: string; onState: (state: MapState, ms?: number) => void }) {
  const box = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLDivElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const map = useRef<GoogleMap | null>(null);
  const drawn = useRef<{ markers: Marker[]; lines: Polyline[] }>({ markers: [], lines: [] });
  const started = useRef(0);
  const [seen, setSeen] = useState(false);
  const [maps, setMaps] = useState<Maps | null>(null);
  const [state, setState] = useState<MapState>("waiting");
  const [areasOn, setAreasOn] = useState(true);
  const [open, setOpen] = useState<MapInfo | null>(null);
  const cone = useMarkerCone();
  const opener = useRef<Marker | null>(null);
  const report = useRef(onState);
  report.current = onState;
  useEffect(() => {
    const target = box.current;
    if (!target || seen) return;
    if (!("IntersectionObserver" in window)) {
      setSeen(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => entries.some((entry) => entry.isIntersecting) && setSeen(true), { rootMargin: "200px" });
    observer.observe(target);
    return () => observer.disconnect();
  }, [seen]);
  useEffect(() => {
    if (!seen) return;
    let live = true;
    started.current = performance.now();
    setState("loading");
    user
      .getIdToken()
      .then(loadMaps)
      .then((loaded) => live && setMaps(loaded))
      .catch(() => live && setState("failed"));
    return () => {
      live = false;
    };
  }, [user, seen]);
  useEffect(() => {
    if (!maps || !canvas.current) return;
    if (!map.current) {
      map.current = new maps.maps.Map(canvas.current, {
        center: { lat: 39.8, lng: -98.6 },
        zoom: 4,
        mapId: maps.mapId,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        clickableIcons: false,
      });
      const shown = map.current;
      const waits = ["tilesloaded", "idle"].map((event) =>
        shown.addListener(event, () => {
          for (const wait of waits) wait.remove();
          setState("ready");
          report.current("ready", since(started.current));
        }),
      );
    }
    const view = map.current;
    for (const marker of drawn.current.markers) marker.map = null;
    for (const line of drawn.current.lines) line.setMap(null);
    const markers: Marker[] = [];
    const lines: Polyline[] = [];
    let unplaced = 0;
    const add = (position: api.LatLng, title: string, content: HTMLElement, zIndex: number, click?: string | MapInfo) => {
      if (!placeable(position)) {
        unplaced += 1;
        return;
      }
      const marker = new maps.marker.AdvancedMarkerElement({ map: view, position, title, content, zIndex, gmpClickable: Boolean(click) });
      if (typeof click === "string") marker.addListener("click", () => navigate(click));
      else if (click) {
        marker.addListener("click", () => {
          opener.current = marker;
          setOpen(click);
        });
        cone.watch(content, click.accountId, `${click.title} · ${click.name}`, { words: click.fillText });
      }
      markers.push(marker);
    };
    const colours = new Map(found.routes.map((route) => [route.id, truckColour(route.colour)]));
    const trucks = new Map(found.routes.map((route) => [route.id, route.truck]));
    const paths = new Map<string, api.LatLng[]>();
    for (const point of found.points) {
      const when = point.at ? ` at ${new Date(point.at).toLocaleTimeString("en-US", { timeZone, hour: "numeric", minute: "2-digit" })}` : point.arrive ? `, planned ${point.arrive}` : "";
      const truck = `Truck ${trucks.get(point.route_id) ?? ""}`;
      const name = `${point.number} ${point.name}`;
      const title = dotTitle([`Stop ${point.position}`, name, truck, `${STATE_TEXT[point.state]}${when}`], point.fill);
      const content = levelDot({ share: point.fill ?? null, number: point.position, alert: point.state === "late", done: FINISHED.has(point.state) });
      const info = { id: `${point.route_id}-${point.position}`, accountId: point.account_id, title: `Stop ${point.position} · ${truck}`, name, share: point.fill, lines: [`${STATE_TEXT[point.state]}${when}`], href: `${base}/accounts/${point.account_id}` };
      add(point, title, content, point.state === "late" ? 900 : 500 + point.position, info);
      if (placeable(point)) paths.set(point.route_id, [...(paths.get(point.route_id) ?? []), point]);
    }
    for (const [routeId, path] of paths) {
      const line = new maps.maps.Polyline({ strokeColor: colours.get(routeId), strokeOpacity: 0.55, strokeWeight: 2, clickable: false });
      line.setPath(path);
      line.setMap(view);
      lines.push(line);
    }
    for (const route of found.routes) {
      if (!route.last_stop) continue;
      const time = new Date(route.last_stop.at).toLocaleTimeString("en-US", { timeZone, hour: "numeric", minute: "2-digit" });
      const title = `Truck ${route.truck}: last report ${time} at ${route.last_stop.number}. This is not a live position.`;
      add(route.last_stop, title, element(`ov-truck${route.quiet ? " quiet" : ""}`, `${route.truck} · ${time}`, truckColour(route.colour)), 2000, `${base}${routesPath(found.day, route.truck)}`);
    }
    for (const tank of found.tanks.points) {
      const side = tankSide(tank);
      const said = tank.state === "full" ? floatWords(true, tank.float_pct) : ["Sensor alert", side !== null && floatWords(side, tank.float_pct)].filter(Boolean).join(" · ");
      const share = side === null ? (tank.fill ?? null) : floatShare(side, tank.float_pct, tank.fill);
      const shown = share !== null && (side === null || floatAgrees(side, tank.float_pct, tank.fill));
      const words = [said, shown && `estimate ${fillWords(share).toLowerCase()}`].filter(Boolean).join(" · ");
      const name = tank.name ? accountName(tank.number, tank.name) : `${tank.number} ${tank.label}`;
      const info = { id: `tank-${tank.account_id}-${tank.label}`, accountId: tank.account_id, title: name, name: "Container", share, fillText: words, href: `${base}/accounts/${tank.account_id}` };
      add(tank, `${name} · ${words}`, levelDot({ share, alert: tank.state === "alarm" }), 1500, info);
    }
    drawn.current = { markers, lines };
    if (unplaced) console.info(`overview: the map leaves out ${unplaced} points with no valid position`);
  }, [maps, found, base, timeZone]);
  const today = useMemo(() => todayPoints(found), [found]);
  const pins = usePinLayer(maps, map, user, {
    kind: "accounts",
    layer: "door",
    filters: ALL_ACCOUNTS,
    enabled: maps !== null,
    skip: today.accounts,
    zIndex: PIN_Z,
    watch: cone.watch,
    onPin: (pin, marker) => {
      opener.current = marker;
      setOpen({
        id: pin.id,
        accountId: pin.id,
        title: statusLabel("accounts", pin.status),
        name: accountName(pin.number, pin.name),
        share: pin.fill,
        fillText: pin.float ? sideWords(pin.float, pin.fill) : undefined,
        lines: [pin.check && CHECK_PIN, pin.address],
        href: `${base}/accounts/${pin.id}`,
      });
    },
    fallback: today.points,
  });
  const { count: areaCount } = useAreaShapes(maps, map, user, areasOn, { faint: true });
  useEffect(() => {
    const target = canvas.current;
    if (!maps || !target) return;
    return watchMapErrors(target, () => setState("failed"));
  }, [maps]);
  useEffect(() => {
    if (state === "failed") report.current("failed", since(started.current));
  }, [state]);
  useEffect(
    () => () => {
      for (const marker of drawn.current.markers) marker.map = null;
      for (const line of drawn.current.lines) line.setMap(null);
    },
    [],
  );
  return (
    <div ref={box} className="ov-map-box">
      <div className="ov-map-bar">
        <div className="ov-legend" role="group" aria-label="Map key">
          <div className="ov-legend-group">
            <span className="ov-legend-head" id="ov-key-today">
              Today:
            </span>
            <ul aria-labelledby="ov-key-today">
              <li>
                <LevelDot share={0.6} number={3} small /> <HelpText text="The number is the place of the stop on the route.">Stop of today</HelpText>
              </li>
              <li>
                <LevelDot share={0.6} done small /> Done, measured or skipped
              </li>
              <li>
                <LevelDot share={0.6} alert small /> Late stop or sensor alert
              </li>
              <li>
                <span className="ov-key ov-truck-key" aria-hidden="true" /> <HelpText text="The place of the last report of the driver. It is not a live position.">Last report</HelpText>
              </li>
            </ul>
          </div>
          {pins.list !== null && (
            <div className="ov-legend-group" data-testid="ov-key-accounts">
              <span className="ov-legend-head" id="ov-key-accounts">
                Accounts:
              </span>
              <ul aria-labelledby="ov-key-accounts">
                <FillBandKeys />
                <li>
                  <LevelDot share={null} small /> No estimate
                </li>
                <ClusterKey />
              </ul>
            </div>
          )}
        </div>
        {areaCount > 0 && <AreaToggle on={areasOn} onChange={setAreasOn} />}
      </div>
      <div ref={wrap} className="ov-map-wrap">
        <div ref={canvas} className="pin-map-canvas ov-map" data-testid="ov-map" data-state={state} role="region" aria-label="Map of the accounts and the stops of today" />
        {state === "ready" && <MapFullscreenButton wrap={wrap} testId="ov-map-fullscreen" />}
        {state !== "ready" && state !== "failed" && <p className="ov-map-note sub">{state === "waiting" ? "The map loads when it is on the screen." : "Loading the map…"}</p>}
        {state === "failed" && (
          <p className="ov-map-note map-warning" data-testid="ov-map-failed">
            The map cannot load. The table of routes shows the same stops.
          </p>
        )}
        {cone.card}
        {open && (
          <MapInfoBox
            testId="ov-map-box"
            info={open}
            onClose={() => {
              setOpen(null);
              (opener.current as unknown as HTMLElement | null)?.focus?.();
            }}
          />
        )}
      </div>
      {(pins.found === "server" || pins.found === "network" || pins.failed) && (
        <p className="map-warning" role="status" data-testid="ov-pins-failed">
          {pins.failed ? "The account pins did not load again. The map shows the pins of the last check." : "The account pins did not load. The map shows only the stops and containers of today."}{" "}
          <button type="button" className="quiet" onClick={() => !pins.loading && pins.retry()} aria-disabled={pins.loading || undefined} data-testid="ov-pins-retry">
            {pins.loading ? "Loading…" : "Try again"}
          </button>
        </p>
      )}
      {found.stops.no_point > 0 && <p className="sub">The map does not show {found.stops.no_point} stops that have no point.</p>}
    </div>
  );
}

/** A tile is one link, or a box with a link on each line when its lines list different rows. */
function Tile({ id, href, label, value, unit, sub, when, extra, children, tone }: { id: string; href?: string; label: string; value: string; unit?: string; sub: ReactNode; when: string; extra?: ReactNode; children?: ReactNode; tone?: "warn" | "ok" }) {
  const parts = (
    <>
      <span className="ov-label">{label}</span>
      <span className="ov-value">
        {value}
        {unit && <span className="ov-unit"> {unit}</span>}
      </span>
      <span className="ov-sub">{sub}</span>
      <span className="ov-chart">{children}</span>
      {extra && <span className="ov-extra">{extra}</span>}
      <span className="ov-when">{when}</span>
    </>
  );
  const className = `ov-tile${extra ? " two" : ""}${tone ? ` ${tone}` : ""}`;
  return href ? (
    <a className={className} href={href} onClick={go} data-testid={id}>
      {parts}
    </a>
  ) : (
    <div className={className} data-testid={id}>
      {parts}
    </div>
  );
}

/** The day names or ages under a bar chart, as text of the page size. */
function Axis({ labels }: { labels: string[] }) {
  return (
    <span className="ov-axis" aria-hidden="true" style={{ gridTemplateColumns: `repeat(${labels.length}, minmax(0, 1fr))` }}>
      {labels.map((label, n) => (
        <span key={n}>{label}</span>
      ))}
    </span>
  );
}

function Progress({ part, whole, label }: { part: number; whole: number; label: string }) {
  const width = progressWidth(part, whole, CHART_W);
  return (
    <svg viewBox={`0 0 ${CHART_W} 12`} className="ov-svg ov-progress" role="img" aria-label={label} preserveAspectRatio="none">
      <rect x="0" y="2" width={CHART_W} height="8" rx="4" className="ov-track" />
      <rect x="0" y="2" width={width} height="8" rx="4" className="ov-fill" />
    </svg>
  );
}

function StateBar({ states, width = CHART_W, label }: { states: Record<api.OverviewState, number>; width?: number; label: string }) {
  return (
    <svg viewBox={`0 0 ${width} 12`} className="ov-svg ov-states" role="img" aria-label={label} preserveAspectRatio="none">
      <rect x="0" y="2" width={width} height="8" rx="2" className="ov-track" />
      {segments(states, width).map((part) => (
        <rect key={part.state} x={part.x} y="2" width={Math.max(0, part.width - 1)} height="8" className={`ov-seg ${part.state}`} />
      ))}
    </svg>
  );
}

/** The tanks in the field: full in the orange of the map diamond, not reporting in grey stripes. */
function TankBar({ tanks }: { tanks: api.Overview["tanks"] }) {
  const share = (n: number) => (tanks.in_field > 0 ? svgLength((n / tanks.in_field) * CHART_W) : 0);
  const full = share(tanks.full);
  return (
    <>
      <svg viewBox={`0 0 ${CHART_W} 12`} className="ov-svg ov-tankbar" role="img" aria-label={`${tanks.full} full, ${tanks.stale} not reporting, of ${tanks.in_field}`} preserveAspectRatio="none">
        <defs>
          <pattern id="ov-stripes" width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="4" height="4" className="ov-stale-bg" />
            <rect width="2" height="4" className="ov-stale-line" />
          </pattern>
        </defs>
        <rect x="0" y="2" width={CHART_W} height="8" rx="2" className="ov-track" />
        {full > 0 && <rect x="0" y="2" width={full} height="8" className="ov-tank-full" />}
        {tanks.stale > 0 && <rect x={full} y="2" width={share(tanks.stale)} height="8" fill="url(#ov-stripes)" />}
      </svg>
      <span className="ov-tank-key" aria-hidden="true">
        <span>
          <span className="ov-key ov-tank full" /> Full
        </span>
        <span>
          <span className="ov-key ov-stale-key" /> Not reporting
        </span>
      </span>
    </>
  );
}

function WeekBars({ found }: { found: api.Overview }) {
  const top = Math.max(0, ...found.gallons.days, ...found.gallons.days_before);
  const now = bars([...found.gallons.days, ...Array(7 - found.gallons.days.length).fill(0)], CHART_W, CHART_H - 12, 6, top);
  const before = bars(found.gallons.days_before, CHART_W, CHART_H - 12, 6, top);
  const names = dayNames(found.gallons.week_start);
  return (
    <>
      <svg viewBox={`0 0 ${CHART_W} ${CHART_H - 12}`} className="ov-svg ov-axis-svg" preserveAspectRatio="none" role="img" aria-label={`Gallons each day this week, with the same day of last week: ${names.map((name, n) => `${name} ${galWhole(found.gallons.days[n] ?? 0)} (last week ${galWhole(found.gallons.days_before[n] ?? 0)})`).join(", ")}`}>
        {before.map((bar, n) => (
          <rect key={`b${n}`} x={bar.x} y={bar.y} width={bar.width} height={bar.height} className="ov-ghost" />
        ))}
        {now.map((bar, n) => n < found.gallons.days.length && <rect key={`n${n}`} x={bar.x + bar.width * 0.25} y={bar.y} width={bar.width * 0.5} height={bar.height} className="ov-bar" />)}
      </svg>
      <Axis labels={names.map((name) => name.slice(0, 2))} />
    </>
  );
}

function EightWeeks({ weeks }: { weeks: api.OverviewWeek[] }) {
  const top = weekTop(weeks);
  const now = bars(weeks.map((week) => week.gallons), CHART_W, CHART_H - 4, 8, top);
  const before = bars(weeks.map((week) => week.before_gal), CHART_W, CHART_H - 4, 8, top);
  return (
    <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} className="ov-svg" role="img" aria-label={`Gallons of each of the last 8 weeks, the oldest first: ${weeksLabel(weeks)}`}>
      {now.map((bar, n) => (
        <rect key={n} x={bar.x} y={bar.y} width={bar.width} height={bar.height} className="ov-bar" />
      ))}
      {before.map((bar, n) => (
        <line key={`b${n}`} x1={bar.x - 2} x2={bar.x + bar.width + 2} y1={bar.y} y2={bar.y} className="ov-before" />
      ))}
    </svg>
  );
}

function AgeBars({ ages }: { ages: number[] }) {
  const found = bars(ages, CHART_W, CHART_H - 12, 12);
  return (
    <>
      <svg viewBox={`0 0 ${CHART_W} ${CHART_H - 12}`} className="ov-svg ov-axis-svg" preserveAspectRatio="none" role="img" aria-label={`Open issues by age: ${ages.map((n, i) => `${AGE_TEXT[i]} ${n}`).join(", ")}`}>
        {found.map((bar, n) => (
          <rect key={n} x={bar.x} y={bar.y} width={bar.width} height={bar.height} className={n >= 2 ? "ov-bar warn" : "ov-bar"} />
        ))}
      </svg>
      <Axis labels={["<1 d", "1–7 d", "7–30 d", ">30 d"]} />
    </>
  );
}

const ROUTE_COLUMNS: SortColumn<api.OverviewRoute>[] = [
  { key: "truck", label: "Truck", value: (r) => r.truck },
  { key: "driver", label: "Driver", value: (r) => shortName(r.driver_email) },
  { key: "status", label: "Status", value: (r) => routeStatusText[r.status] },
  { key: "stops", label: "Stops reported", value: (r) => (r.stops ? reported(r.states) / r.stops : null), first: "desc", num: true },
  { key: "late", label: "Late", value: (r) => r.states.late, first: "desc", num: true },
  { key: "gallons", label: "Gallons", value: (r) => r.gallons, first: "desc", num: true },
  { key: "report", label: "Last report", value: (r) => r.last_report_at, first: "desc" },
];

// From 641 to 1024 px, the CSS hides the status column: then the table fits (SO-1050).
const COLUMN_CLASS: Record<string, string> = { status: "ov-col-status" };

function RouteTable({ found, base, now, timeZone }: { found: api.Overview; base: string; now: Date; timeZone?: string }) {
  const [sort, setSort] = useSort("routes-sort", ROUTE_COLUMNS);
  const rows = sort ? sortRows(found.routes, ROUTE_COLUMNS, sort) : found.routes;
  const link = `${base}${routesPath(found.day)}`;
  return (
    <section className="ov-routes" aria-labelledby="ov-routes-title" data-testid="ov-routes">
      <h2 id="ov-routes-title">
        <a href={link} onClick={go}>
          Drivers and routes today
        </a>
      </h2>
      {found.routes.length === 0 ? (
        <p className="sub" data-testid="ov-no-routes">
          No routes today.{" "}
          <a href={link} onClick={go}>
            Open Routes
          </a>
        </p>
      ) : (
        <>
          <SortSelect id="routes-sort" columns={ROUTE_COLUMNS} sort={sort} onSort={setSort} fallback="Truck" />
          <div className="table-scroll" tabIndex={0} role="region" aria-label="The routes of today">
            <table className="list ov-table" data-testid="ov-route-table">
              <thead>
                <tr>
                  {ROUTE_COLUMNS.map((column) => (
                    <SortTh key={column.key} column={column} sort={sort} onSort={setSort} className={COLUMN_CLASS[column.key]} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((route) => (
                  <tr key={route.id} data-testid="ov-route" data-truck={route.truck} data-quiet={route.quiet}>
                    <th scope="row" data-label="Truck">
                      <a href={`${base}${routesPath(found.day, route.truck)}`} onClick={go} aria-label={`Truck ${route.truck} on Routes`}>
                        <span className="route-stop-number" style={{ background: truckColour(route.colour) }}>
                          {route.truck}
                        </span>
                      </a>
                    </th>
                    <td className="ov-driver" data-label="Driver" title={route.driver_email ?? undefined}>
                      {shortName(route.driver_email)}
                    </td>
                    <td className="ov-col-status" data-label="Status">
                      {routeStatusText[route.status]}
                    </td>
                    <td className="num ov-stops-cell" data-label="Stops reported">
                      <span>{routeText(route)}</span>
                      <StateBar states={route.states} width={BAR_W} label={`Truck ${route.truck}: ${STATES.map((s) => `${STATE_TEXT[s]} ${route.states[s]}`).join(", ")}`} />
                    </td>
                    <td className={route.states.late ? "num ov-late" : "num"} data-label="Late">
                      {route.states.late}
                    </td>
                    <td className="num" data-label="Gallons">
                      {galWhole(route.gallons)}
                      {route.planned_gal > 0 && <span className="sub ov-gal-of"> of {galWhole(route.planned_gal)}</span>}
                    </td>
                    <td data-label="Last report" className={route.quiet ? "ov-report ov-quiet" : "ov-report"}>
                      {reportText(route.last_report_at, now, timeZone)}
                      {route.quiet && <span className="chip-flag serious"> No report for {found.rules.quiet_h} h</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function Status({ found, base }: { found: api.Overview; base: string }) {
  const ok = found.looks.length === 0;
  return (
    <section className={`ov-status ${ok ? "ok" : "look"}`} role="status" data-testid="overview-status" data-ok={ok}>
      <span className="ov-status-mark" aria-hidden="true">
        {ok ? "✓" : "!"}
      </span>
      <div>
        <strong>{statusTitle(found)}</strong>
        {ok ? (
          <span className="ov-status-text"> {wellText(found)}</span>
        ) : (
          <ul className="ov-looks">
            {found.looks.map((look) => (
              <li key={look.code} data-testid={`look-${look.code}`}>
                {look.code === "overflow" ? (
                  <>
                    {lookText(look.code, look.count, [], found.rules).replace(/\.$/, ": ")}
                    {found.overflows.map((call, n) => (
                      <span key={`${call.account_id}${n}`}>
                        {n > 0 && ", "}
                        <a href={`${base}/accounts/${call.account_id}`} onClick={go}>
                          {call.number}
                        </a>
                      </span>
                    ))}
                    .
                  </>
                ) : (
                  <a href={`${base}${lookPath(look.code, found.day, look.names)}`} onClick={go}>
                    {lookText(look.code, look.count, look.names, found.rules)}
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function Tiles({ found, base, when, today }: { found: api.Overview; base: string; when: string; today: string }) {
  const g = found.gallons;
  const s = found.stops;
  const done = reported(s);
  const lastGal = g.weeks.reduce((sum, w) => sum + w.gallons, 0);
  const beforeGal = g.weeks.reduce((sum, w) => sum + w.before_gal, 0);
  const stops = g.today_stops;
  const routeText = routeGalText(stops, g.today_planned);
  useEffect(() => {
    if (stops === undefined) console.info("overview: the API sent no gallons.today_stops, so the route gallons are not shown");
  }, [stops]);
  return (
    <div className="ov-tiles">
      <Tile
        id="ov-gallons-today"
        label="Route gallons today"
        value={stops === undefined ? "–" : galShort(stops)}
        unit={stops === undefined ? undefined : "gal"}
        sub={
          <a href={`${base}${routesPath(found.day)}`} onClick={go} data-testid="ov-gallons-stops">
            {routeText}
          </a>
        }
        extra={
          <a href={`${base}/pickups?day=${found.day}`} onClick={go} data-testid="ov-gallons-all">
            {allPickupsText(g.today)}
          </a>
        }
        when={`Today to ${when}`}
      >
        {stops !== undefined && <Progress part={stops} whole={g.today_planned} label={routeText} />}
      </Tile>
      <Tile id="ov-week" href={`${base}/scorecard?weeks=8`} label="This week" value={galShort(g.week)} unit="gal" sub={changeText(g.week, g.week_before, "the same days of last week")} when={`${dayTitle(g.week_start, today).replace(" (today)", "")} to today`}>
        <WeekBars found={found} />
      </Tile>
      <Tile id="ov-weeks" href={`${base}/scorecard?weeks=8`} label="Last 8 weeks" value={galShort(lastGal)} unit="gal" sub={changeText(lastGal, beforeGal, "the 8 weeks before")} when={`8 full weeks from ${dateText(g.weeks[0].week_start)}`}>
        <EightWeeks weeks={g.weeks} />
      </Tile>
      <Tile
        id="ov-stops"
        href={`${base}${routesPath(found.day)}`}
        label="Stops reported today"
        value={`${done} of ${s.planned}`}
        unit="stops"
        sub={STATES.filter((state) => s[state]).map((state) => `${STATE_TEXT[state]} ${s[state]}`).join(" · ") || "No stops today"}
        when={`${found.routes.length} ${found.routes.length === 1 ? "route" : "routes"} · to ${when}`}
        tone={s.late ? "warn" : undefined}
      >
        <StateBar states={s} label={STATES.map((state) => `${STATE_TEXT[state]} ${s[state]}`).join(", ")} />
      </Tile>
      <Tile
        id="ov-issues"
        href={`${base}/issues?view=all`}
        label="Open issues"
        value={found.issues.open.toLocaleString("en-US")}
        unit={found.issues.open === 1 ? "issue" : "issues"}
        sub={`${found.issues.overdue} after the due date · ${found.issues.alerts} sensor alerts`}
        when={`Open at ${when}`}
        tone={found.issues.new_alerts ? "warn" : undefined}
      >
        <AgeBars ages={found.issues.ages} />
      </Tile>
      <Tile id="ov-tanks" href={`${base}/tanks`} label="Containers with a sensor" value={found.tanks.full.toLocaleString("en-US")} unit="full" sub={`${found.tanks.stale} not reporting for ${found.rules.stale_h} h · ${found.tanks.in_field} in the field`} when={`Last readings, at ${when}`}>
        <TankBar tanks={found.tanks} />
      </Tile>
    </div>
  );
}

/** A frame of the same size as the page, so the numbers do not move the page when they come. */
function OverviewSkeleton() {
  return (
    <SkeletonFrame shape="overview" label="Loading the numbers">
      <section className="ov-status">
        <SkeletonBar width="40%" />
      </section>
      <div className="ov-tiles">
        {Array.from({ length: 6 }, (_, n) => (
          <div key={n} className="ov-tile">
            <SkeletonBar width="55%" />
            <SkeletonBar width="35%" className="skeleton-big" />
          </div>
        ))}
      </div>
      <div className="ov-main">
        <div className="ov-map-box">
          <div className="ov-map-bar" />
          <div className="pin-map-canvas ov-map skeleton-fill" />
        </div>
        <div className="ov-routes" />
      </div>
    </SkeletonFrame>
  );
}

/** The time of the numbers: the server time after a check, else the age of the kept numbers (rule U-36). */
export function OverviewFreshness({ view, timeZone }: { view: Pick<ViewState<api.Overview>, "at" | "data" | "checking" | "checked" | "error">; timeZone?: string }) {
  if (view.at === null || !view.data) return null;
  if (view.error && !view.checking) return <span data-testid="view-stale"> · {failedText(view.at, timeZone)}</span>;
  if (view.checked && !view.checking) return <span data-testid="view-updated"> · Updated {clockTime(new Date(view.data.now), timeZone)}</span>;
  return <span data-testid="view-kept"> · {keptText(view.at)}</span>;
}

export function OverviewPage({ base, org, auth, user, canView }: { base: string; org: api.Bootstrap; auth: Auth; user: User; canView: boolean | undefined }) {
  const logged = useRef({ numbers: false, map: false });
  useTitle(`Overview · ${org.name}`);
  const view = useView(canView === true ? OVERVIEW.name : null, async (signal) => {
    const start = performance.now();
    try {
      const next = await api.overview(await user.getIdToken(), signal);
      if (!logged.current.numbers) {
        logged.current.numbers = true;
        send(user, { numbers_ms: since(start) });
      }
      return next;
    } catch (e) {
      const code = e instanceof api.ApiError ? e.status : 0;
      if (!signal.aborted) {
        console.warn("overview: the numbers did not load", code);
        send(user, { failed: ["numbers"], status: code, numbers_ms: since(start) });
      }
      throw e;
    }
  });
  const found: Load = view.data ?? (view.error && !view.checking ? failure(view.error instanceof api.ApiError ? view.error.status : 0) : undefined);
  const reload = view.reload;
  const onMap = useCallback(
    (state: MapState, ms?: number) => {
      if (logged.current.map) return;
      logged.current.map = true;
      send(user, state === "failed" ? { failed: ["map"], map_ms: ms } : { map_ms: ms });
    },
    [user],
  );
  if (canView === false) {
    return (
      <main data-testid="overview-hidden">
        <h1 tabIndex={-1}>Overview</h1>
        <p>Your role does not show the overview.</p>
      </main>
    );
  }
  const shown = typeof found === "object" ? found : undefined;
  const failedNow = view.error && !view.checking ? refreshFailure(view.error) : null;
  const now = shown ? new Date(shown.now) : new Date();
  const when = clockTime(now, org.time_zone);
  return (
    <main className="wide overview-page" data-testid="overview-page" data-state={shown ? "loaded" : typeof found === "string" ? found : "loading"}>
      <div className="ov-head">
        <h1 tabIndex={-1}>Overview</h1>
        <p className="sub" data-testid="overview-updated">
          {shown ? dayTitle(shown.day, shown.day) : "Today"}
          {shown && view.at !== null && <OverviewFreshness view={view} timeZone={org.time_zone} />}
        </p>
      </div>
      {shown && failedNow && <RefreshFailed failed={failedNow} at={view.at} timeZone={org.time_zone} auth={auth} retry={reload} />}
      {found === "session" ? (
        <p role="alert" className="error">
          Your session ended.{" "}
          <button type="button" onClick={() => signOut(auth)}>
            Sign in again
          </button>
        </p>
      ) : typeof found === "string" ? (
        <p role="alert" className="error" data-testid="overview-failed">
          {FAILURE_TEXT[found]}{" "}
          <button type="button" onClick={reload}>
            Try again
          </button>
        </p>
      ) : !shown ? (
        <OverviewSkeleton />
      ) : (
        <>
          <Status found={shown} base={base} />
          <Tiles found={shown} base={base} when={when} today={shown.day} />
          <div className="ov-main">
            <OverviewMap user={user} found={shown} base={base} timeZone={org.time_zone} onState={onMap} />
            <RouteTable found={shown} base={base} now={now} timeZone={org.time_zone} />
          </div>
          <p className="hint">
            The page shows &quot;Needs a look&quot; for these problems. A route has open stops and no report for {shown.rules.quiet_h} h, from {shown.rules.day_start} to {shown.rules.day_end}. A stop is open more than {shown.rules.late_min} min after its planned time. Today has an overflow call. A sensor alert opened in the last {shown.rules.alert_h} h. The map shows the last stop that each truck reported. It is not a live position.
          </p>
        </>
      )}
    </main>
  );
}
